import type { AllApiError, RetrieveInviteError } from '@frontend/api-types';
import { INVITE_ERROR_DETAIL } from '@frontend/api-types';
import { match } from '@frontend/duck-tape';

type InviteErrorData = RetrieveInviteError['data'];
type ApiErrorData = AllApiError['data'];

export type InviteErrorState = {
  description?: string;
  renderMode: 'inline' | 'page';
};

export const DEFAULT_INVITE_ERROR_STATE: InviteErrorState = {
  description: undefined,
  renderMode: 'page',
};

const isApiErrorData = (errorData: unknown): errorData is ApiErrorData =>
  typeof errorData === 'object' && errorData !== null && 'code' in errorData && typeof errorData.code === 'number';

const isInviteErrorData = (errorData: ApiErrorData): errorData is InviteErrorData =>
  errorData.code === 4041 || errorData.code === 4042;

export const getInviteErrorState = (errorData: ApiErrorData): InviteErrorState =>
  isInviteErrorData(errorData)
    ? match<InviteErrorData, InviteErrorState>(errorData)
        .with({ code: 4042, detail: INVITE_ERROR_DETAIL.INVITE_ALREADY_ACCEPTED }, () => ({
          description: 'Invite already claimed, login to finish sign up',
          renderMode: 'page',
        }))
        .with({ code: 4042, detail: INVITE_ERROR_DETAIL.INVITE_EXPIRED }, () => ({
          description: 'This invite has expired. Please ask the account owner to send you a new invite.',
          renderMode: 'inline',
        }))
        .with({ code: 4041 }, () => ({ description: 'Invite is invalid, please contact support', renderMode: 'page' }))
        .otherwise(() => DEFAULT_INVITE_ERROR_STATE)
    : DEFAULT_INVITE_ERROR_STATE;

export const getInviteErrorDescription = (errorData: ApiErrorData): string | undefined =>
  getInviteErrorState(errorData).description;

export const getInviteErrorStateFromError = (error: unknown): InviteErrorState => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return DEFAULT_INVITE_ERROR_STATE;
  }

  return isApiErrorData(error.data) ? getInviteErrorState(error.data) : DEFAULT_INVITE_ERROR_STATE;
};
